import { useEffect, useRef, useState } from "react";
import { reportPlatformAlert } from "../platformAlerts";

// Espera entre intentos: rápida al principio (un deploy de Daphne tira todos
// los WebSockets y vuelve en segundos), con tope para no martillear si el
// backend está caído de verdad.
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];
// Tras este número de intentos seguidos sin abrir (~1 min) se registra una
// alerta de plataforma en el equipo.
const ALERT_AFTER_FAILURES = 6;

function wsBase() {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = process.env.REACT_APP_BACKEND_URL
    ? new URL(process.env.REACT_APP_BACKEND_URL).host
    : window.location.host;
  return `${proto}//${host}`;
}

/**
 * WebSocket que se reconecta solo mientras el componente esté montado.
 *
 * Antes cada sección abría el socket una vez y, al cerrarse (deploy, corte
 * de red, proxy), quedaba "desconectado" hasta salir y volver a entrar.
 * El token se lee en cada intento, así que una reconexión después de un
 * refresh de sesión usa el access nuevo.
 *
 * `onMessage` recibe el JSON ya parseado. Se guarda en un ref para que un
 * handler nuevo no obligue a reconectar.
 */
export default function useReconnectingWebSocket({
  path,
  onMessage,
  enabled = true,
  pduiId = null,
  label = "WebSocket",
}) {
  const [connected, setConnected] = useState(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!enabled || !path) return undefined;

    let active = true;
    let ws = null;
    let timer = null;
    let failures = 0;

    const scheduleReconnect = () => {
      if (!active) return;
      const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
      if (failures === ALERT_AFTER_FAILURES) {
        reportPlatformAlert(
          pduiId,
          "ws_reconnect_failed",
          `${label}: sin conexión tras ${failures} intentos seguidos`,
        );
      }
      timer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (!active) return;
      const token = localStorage.getItem("access");
      let opened = false;
      try {
        ws = new WebSocket(`${wsBase()}${path}?token=${token}`);
      } catch (e) {
        failures += 1;
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        opened = true;
        failures = 0;
        if (active) setConnected(true);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onMessageRef.current) onMessageRef.current(data, event);
        } catch (e) {
          // mensaje no JSON: se ignora
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          // ya cerrado
        }
      };
      ws.onclose = () => {
        if (!active) return;
        setConnected(false);
        // Un socket que llegó a abrirse y se cayó no cuenta como fallo:
        // se reintenta enseguida. Solo los intentos que no abren escalan.
        if (!opened) failures += 1;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch (e) {
          // ya cerrado
        }
      }
    };
  }, [enabled, path, pduiId, label]);

  return connected;
}
